import { qk } from "@/api/queryKeys";
import { useApiQuery } from "@/api/useApiQuery";
import { useUserKey } from "@/api/useUserKey";
import { getClinicianDetails } from "@/service/reqUrl.service";

import type { ClinicianDetailsParams } from "./types";

interface UseClinicianDetailsResult {
  /** Raw rows from the SP (array), kept unshaped — the table normalizes. */
  data: unknown;
  loading: boolean;
  error: string | null;
}

/**
 * Fires `POST /dashboard/clinician-details` for the Patient Notifications
 * By-Clinician ETA table. The caller passes `params: null` until BOTH the
 * dashboard-metrics and discipline-summary calls have completed, so this request
 * only goes out once — chained behind the rest of the dashboard. Re-fires when
 * the date range or any filter in `params` changes.
 *
 * That chaining is unchanged: `enabled` is driven by the same `params != null`
 * the caller already computed from `clinicianReady`.
 */
export function useClinicianDetails(
  params: ClinicianDetailsParams | null,
): UseClinicianDetailsResult {
  const userKey = useUserKey();

  const { data, loading, error } = useApiQuery<unknown>({
    queryKey: qk.clinicianDetails(userKey, params),
    url: getClinicianDetails,
    body: params,
    enabled: params != null,
    fallbackMessage: "Failed to fetch clinician details",
  });

  return { data, loading, error };
}
